import React from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import { LottieOverlay } from "@/components/editor/types";
import { animationTemplates } from "@/components/editor/templates/animation-templates";
import { Lottie } from "@remotion/lottie";

/**
 * Props for the LottieLayerContent component
 * @interface LottieLayerContentProps
 * @property {LottieOverlay} overlay - The lottie overlay object containing source and style information
 */
interface LottieLayerContentProps {
  overlay: LottieOverlay;
}

export const LottieLayerContent: React.FC<LottieLayerContentProps> = ({
  overlay,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const isExitPhase = frame >= overlay.durationInFrames - 30;

  /**
   * Apply enter animation only during entry phase
   */
  const enterAnimation =
      !isExitPhase && overlay.styles.animation?.enter
        ? animationTemplates[overlay.styles.animation?.enter?.name ?? ""]?.enter?.(
            frame,
            overlay.durationInFrames,
            fps,
            overlay.styles.animation?.enter
          )
        : {};

  /**
   * Apply loop animation only during entry phase
   */
    const loopAnimation = animationTemplates[overlay.styles.animation?.loop?.name ?? ""]?.loop?.(
      frame,
      overlay.durationInFrames,
      fps,
      overlay.styles.animation?.loop
    )
  ?? {};

  /**
   * Apply exit animation only during exit phase
   */
  const exitAnimation =
      isExitPhase && overlay.styles.animation?.exit
        ? animationTemplates[overlay.styles.animation?.exit?.name ?? ""]?.exit?.(
            frame,
            overlay.durationInFrames,
            fps,
            overlay.styles.animation?.exit
          )
        : {};

  /**
   * Combine base styles with current animation phase
   */
  const lottieStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: overlay.styles.objectFit || "cover",
    objectPosition: overlay.styles.objectPosition,
    opacity: overlay.styles.opacity,
    transform: overlay.styles.transform || "none",
    filter: overlay.styles.filter || "none",
    borderRadius: overlay.styles.borderRadius || "0px",
    boxShadow: overlay.styles.boxShadow || "none",
    border: overlay.styles.border || "none",
    ...(isExitPhase ? exitAnimation : enterAnimation),
    ...loopAnimation
  };

  return <Lottie animationData={overlay.content} style={lottieStyle} loop={overlay.loop??false} />
};
